<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <teleport
    v-if="isDesktop && teleportReady"
    to=".oxd-topbar-header-userarea > ul"
  >
    <li class="orangehrm-topbar-notifications-host">
      <div class="orangehrm-topbar-notifications">
        <div class="orangehrm-topbar-notifications-wrap">
          <button
            type="button"
            class="orangehrm-topbar-notifications-trigger"
            :aria-label="$t('buzz.buzz_notifications')"
            :aria-expanded="isOpen"
            @click="onToggle"
          >
            <oxd-icon name="bell" class="orangehrm-topbar-notifications-icon" />
            <span
              v-if="unreadCount > 0"
              class="orangehrm-topbar-notifications-badge"
            >
              {{ unreadCount > 99 ? '99+' : unreadCount }}
            </span>
          </button>

          <div
            v-if="isOpen"
            v-click-outside="onClose"
            class="orangehrm-topbar-notifications-panel"
            role="dialog"
            :aria-label="$t('buzz.buzz_notifications')"
          >
            <div class="orangehrm-topbar-notifications-panel-header">
              <oxd-text
                type="card-title"
                class="orangehrm-topbar-notifications-title"
              >
                {{ $t('buzz.buzz_notifications') }}
              </oxd-text>
              <oxd-button
                v-if="notifications.length > 0 && unreadCount > 0"
                display-type="ghost"
                size="small"
                :label="$t('buzz.mark_all_as_read')"
                @click="onMarkAllRead"
              />
            </div>

            <oxd-loading-spinner
              v-if="isLoading"
              class="orangehrm-topbar-notifications-loader"
            />

            <div v-else class="orangehrm-topbar-notifications-list">
              <button
                v-for="item in notifications"
                :key="item.id"
                type="button"
                class="orangehrm-topbar-notifications-item"
                :class="{'is-unread': !item.isRead}"
                @click="onNotificationClick(item)"
              >
                <profile-image
                  :employee="item.performer"
                  class="orangehrm-topbar-notifications-avatar"
                />
                <div class="orangehrm-topbar-notifications-body">
                  <oxd-text
                    tag="p"
                    class="orangehrm-topbar-notifications-message"
                  >
                    {{ notificationMessage(item) }}
                  </oxd-text>
                  <oxd-text
                    v-if="item.contentExcerpt"
                    tag="p"
                    class="orangehrm-topbar-notifications-excerpt"
                  >
                    {{ item.contentExcerpt }}
                  </oxd-text>
                  <oxd-text tag="p" class="orangehrm-topbar-notifications-time">
                    {{ formatNotificationTime(item) }}
                  </oxd-text>
                </div>
              </button>

              <div v-if="isEmpty" class="orangehrm-topbar-notifications-empty">
                <oxd-icon
                  name="bell"
                  class="orangehrm-topbar-notifications-empty-icon"
                />
                <oxd-text tag="p">{{ $t('buzz.no_notifications') }}</oxd-text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>
  </teleport>
</template>

<script>
import {ref, computed, onMounted, onBeforeUnmount} from 'vue';
import {
  clickOutsideDirective,
  OxdIcon,
  OxdButton,
  OxdText,
  useResponsive,
  DEVICE_TYPES,
} from '@ohrm/oxd';
import useBuzzNotifications from '@/orangehrmBuzzPlugin/util/composable/useBuzzNotifications';
import ProfileImage from '@/orangehrmBuzzPlugin/components/ProfileImage';

const TOPBAR_TELEPORT_SELECTOR = '.oxd-topbar-header-userarea > ul';

export default {
  name: 'TopbarNotificationBell',

  components: {
    'oxd-icon': OxdIcon,
    'oxd-button': OxdButton,
    'oxd-text': OxdText,
    'profile-image': ProfileImage,
  },

  directives: {
    'click-outside': clickOutsideDirective,
  },

  setup() {
    const responsiveState = useResponsive();
    const teleportReady = ref(false);
    const isOpen = ref(false);

    const {
      isLoading,
      notifications,
      unreadCount,
      isEmpty,
      fetchNotifications,
      notificationMessage,
      formatNotificationTime,
      markAsRead,
      onMarkAllRead,
    } = useBuzzNotifications({pageSize: 30});

    const isDesktop = computed(() => {
      return (
        responsiveState.screenType === DEVICE_TYPES.DEVICE_LG ||
        responsiveState.screenType === DEVICE_TYPES.DEVICE_XL
      );
    });

    const onClose = () => {
      isOpen.value = false;
    };

    const onToggle = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        fetchNotifications();
      }
    };

    const onNotificationClick = (item) => {
      markAsRead(item).finally(() => {
        isOpen.value = false;
        window.dispatchEvent(
          new CustomEvent('orangehrm-buzz-notification-target', {
            detail: {notification: item},
          }),
        );
      });
    };

    let teleportObserver = null;

    onMounted(() => {
      const checkTeleportTarget = () => {
        teleportReady.value = !!document.querySelector(
          TOPBAR_TELEPORT_SELECTOR,
        );
      };
      checkTeleportTarget();
      teleportObserver = new MutationObserver(checkTeleportTarget);
      teleportObserver.observe(document.body, {childList: true, subtree: true});
      fetchNotifications();
    });

    onBeforeUnmount(() => {
      teleportObserver?.disconnect();
    });

    return {
      isDesktop,
      teleportReady,
      isOpen,
      isLoading,
      notifications,
      unreadCount,
      isEmpty,
      notificationMessage,
      formatNotificationTime,
      onToggle,
      onClose,
      onNotificationClick,
      onMarkAllRead,
    };
  },
};
</script>

<style src="./topbar-notification-bell.scss" lang="scss" scoped></style>
